import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api.js';

const manilaDay = (offsetDays = 0) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date(Date.now() + offsetDays * 86_400_000));
const num = (v, unit = '') => (v == null ? '—' : `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 })}${unit ? ` ${unit}` : ''}`);
// "Oct 06, 2026, 10:00 PM" — the way Harbor Star's reports write dates.
const when = (ts) =>
  ts ? new Date(ts).toLocaleString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const svgSrc = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
const STATUS = { sent: ['bg-success', 'Sent'], simulated: ['bg-secondary', 'Simulated'], failed: ['bg-danger', 'Failed'] };

function Tile({ label, value, tone = '', testid }) {
  return (
    <div className="col-6 col-lg-3 mb-3">
      <div className="border rounded p-3 h-100 bg-white">
        <div className={`fs-4 fw-bold ${tone}`} data-testid={testid}>{value}</div>
        <div className="small text-muted text-uppercase fw-semibold">{label}</div>
      </div>
    </div>
  );
}

/** The daily management report for a chosen day: the same figures, charts and
 * port summary the morning email carries, plus preview / download / send. */
export default function DailyReportView() {
  const [date, setDate] = useState(() => manilaDay(-1));
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Only the newest request may fill the view: the answer for a day picked
  // earlier can arrive after the answer for the day now on screen.
  const loadSeq = useRef(0);
  const load = useCallback(async (d) => {
    const seq = ++loadSeq.current;
    setError('');
    try {
      const next = await api.getDailyReport(d);
      if (seq === loadSeq.current) setView(next);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setView(null);
      setError(e.details?.[0]?.message || e.message || 'Failed to load the daily report.');
    }
  }, []);
  useEffect(() => {
    setConfirming(false);
    if (date) load(date);
  }, [load, date]);

  const act = async (name, fn) => {
    setMsg(null);
    setBusy(name);
    try {
      await fn();
    } catch (e) {
      setMsg({ type: 'danger', text: e.details?.[0]?.message || e.message || 'That did not work.' });
    } finally {
      setBusy('');
    }
  };
  const preview = () => act('preview', async () => {
    const html = await api.getDailyReportPreview(date);
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  });
  const download = () => act('download', async () => {
    const { blob, filename } = await api.downloadDailyReportWorkbook(date);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  });
  const send = () => act('send', async () => {
    setConfirming(false);
    const run = await api.sendDailyReport(date);
    if (!run) {
      setMsg({ type: 'warning', text: 'A send for this day is already in progress.' });
      return;
    }
    if (run.status === 'sent') setMsg({ type: 'success', text: `Report emailed to ${run.recipientCount} recipient${run.recipientCount === 1 ? '' : 's'}.` });
    else if (run.status === 'simulated') setMsg({ type: 'warning', text: 'Email is not configured on this server, so the send was only simulated.' });
    else setMsg({ type: 'danger', text: `The email could not be sent: ${run.error || 'unknown error'}` });
    await load(date);
  });

  const data = view?.data;
  const t = data?.totals;
  const a = data?.attention;
  const net = t?.netVariance || 0;
  const sentBefore = (view?.runs || []).some((r) => r.status === 'sent');

  return (
    <div className="card border-0 shadow-sm" data-testid="daily-report-view">
      <div className="card-header bg-white pt-3 border-bottom-0 d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Daily Report</h6>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <input type="date" className="form-control form-control-sm w-auto" value={date} max={manilaDay()} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Report date" data-testid="dr-date" />
          <button className="btn btn-sm btn-outline-secondary" onClick={preview} disabled={!data || busy !== ''} data-testid="dr-preview">Preview email</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={download} disabled={!data || busy !== ''} data-testid="dr-download">Download workbook</button>
          {confirming ? (
            <span className="d-inline-flex align-items-center gap-2 border rounded px-2 py-1 bg-light" data-testid="dr-confirm">
              <span className="small">
                Email this report to {view?.recipientCount ?? 0} recipient{view?.recipientCount === 1 ? '' : 's'}?
              </span>
              <button className="btn btn-sm btn-primary" onClick={send} disabled={busy !== ''} data-testid="dr-send-confirm">
                {busy === 'send' ? 'Sending…' : 'Send'}
              </button>
              <button className="btn btn-sm btn-link" onClick={() => setConfirming(false)} disabled={busy !== ''}>Cancel</button>
            </span>
          ) : (
            <button className="btn btn-sm btn-primary" onClick={() => setConfirming(true)} disabled={!data || busy !== ''} data-testid="dr-send">
              {sentBefore ? 'Send again' : 'Send now'}
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger" data-testid="dr-error">{error}</div>}
        {msg && <div className={`alert alert-${msg.type}`} role="alert" data-testid="dr-message">{msg.text}</div>}
        {data && (
          <>
            <div className="row">
              <Tile label="Tugs reported" value={`${t.reported} of ${t.tugs}`} testid="dr-tile-reported" />
              <Tile label="Jobs completed" value={num(t.jobs)} testid="dr-tile-jobs" />
              <Tile label="Fuel consumed" value={num(t.fuelConsumed, 'L')} testid="dr-tile-fuel" />
              <Tile label="Net fuel variance" value={net === 0 ? 'Balanced' : net < 0 ? `${num(-net, 'L')} short` : `${num(net, 'L')} excess`} tone={net < 0 ? 'text-danger' : ''} testid="dr-tile-variance" />
            </div>

            {(a.missing.length > 0 || a.shortages.length > 0 || a.notOperational.length > 0 || a.blackouts.length > 0 || a.followUps.length > 0) && (
              <div className="border-start border-4 border-danger bg-light p-3 mb-3 small" data-testid="dr-attention">
                <div className="fw-bold text-uppercase text-secondary mb-2">Needs attention</div>
                {a.missing.length > 0 && <div><span className="fw-bold text-danger">Reports missing:</span> {a.missing.map((m) => `${m.tugName} (${m.missing.join(' and ')})`).join(', ')}</div>}
                {a.shortages.length > 0 && <div><span className="fw-bold text-danger">Largest fuel shortages:</span> {a.shortages.map((s) => `${s.tugName} ${num(s.litres, 'L')}`).join(' · ')}</div>}
                {a.notOperational.length > 0 && <div><span className="fw-bold">Not operational:</span> {a.notOperational.map((x) => (x.note ? `${x.tugName} — ${x.note}` : x.tugName)).join('; ')}</div>}
                {a.blackouts.length > 0 && <div><span className="fw-bold">Power blackouts:</span> {a.blackouts.map((b) => `${b.tugName} ${num(b.hours, 'h')}`).join(' · ')}</div>}
                {a.followUps.map((f) => <div key={f.tugName}><span className="fw-bold">Follow-up · {f.tugName}:</span> {f.text}</div>)}
              </div>
            )}

            {view.charts.length === 0 && <p className="text-muted small">No figures to chart for this day yet.</p>}
            <div className="row">
              {view.charts.map((c) => (
                <div className="col-xl-6 mb-3" key={c.key}>
                  <img src={svgSrc(c.svg)} alt={c.alt} className="img-fluid border rounded" data-testid="dr-chart" />
                </div>
              ))}
            </div>

            {data.ports.length > 0 && (
              <div className="table-responsive mb-3">
                <table className="table table-sm align-middle small">
                  <thead><tr><th>Port</th><th className="text-end">Jobs</th><th>Tugs</th><th>Weather</th><th>In port</th><th>Expected</th></tr></thead>
                  <tbody>
                    {data.ports.map((p) => (
                      <tr key={p.id} data-testid="dr-port-row">
                        <td className="fw-bold">{p.name}</td>
                        <td className="text-end">{p.jobs}</td>
                        <td>{p.tugNames.join(', ') || '—'}</td>
                        <td>{[p.weather, p.seaCondition].filter(Boolean).join(' · ') || '—'}{p.shoreRemarks ? <div className="text-warning-emphasis">{p.shoreRemarks}</div> : null}</td>
                        <td>{p.inPort.map((v) => v.vesselName).join(', ') || '—'}</td>
                        <td>{p.expected.map((v) => `${v.vesselName} (${when(v.eta)})`).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(data.crewChanges || []).length > 0 && (
              <div className="small mb-3" data-testid="dr-crew-changes">
                <div className="fw-bold text-uppercase text-secondary mb-1">Crew changes</div>
                <ul className="list-unstyled mb-0">
                  {data.crewChanges.flatMap((c) => [
                    ...c.embarked.map((p, i) => (
                      <li key={`${c.tugName}-on-${i}`}><span className="fw-bold">{c.tugName}:</span> {p.name}{p.position ? ` (${p.position})` : ''} embarked {when(p.at)}{p.reason ? ` — ${p.reason}` : ''}</li>
                    )),
                    ...c.disembarked.map((p, i) => (
                      <li key={`${c.tugName}-off-${i}`}><span className="fw-bold">{c.tugName}:</span> {p.name}{p.position ? ` (${p.position})` : ''} disembarked {when(p.at)}{p.reason ? ` — ${p.reason}` : ''}</li>
                    )),
                  ])}
                </ul>
              </div>
            )}

            {data.safety && (
              <div className="small mb-3" data-testid="dr-safety">
                <div className="fw-bold text-uppercase text-secondary mb-1">Safety</div>
                <div>
                  Safe man-hours for the day: <span className="fw-bold" data-testid="dr-safety-day">{num(data.safety.dayHours)}</span>
                  {' · '}Running total: <span className="fw-bold" data-testid="dr-safety-running">{num(data.safety.runningTotal)}</span>
                  {data.safety.since ? ` · ${data.safety.lastLostTimeDate ? 'last lost-time accident' : 'counting since'} ${data.safety.since}` : ''}
                </div>
                {data.safety.incidents.length === 0 ? (
                  <div className="text-success fw-semibold">No accidents or incidents reported.</div>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {data.safety.incidents.map((i) => (
                      <li key={i.id} data-testid="dr-safety-incident">
                        <span className="fw-bold">{i.tugName}:</span> {i.kind}
                        {i.lostTime ? <span className="badge bg-danger ms-1">Lost time</span> : null} — {i.particulars}
                        {i.ncparNo ? ` [NCPAR ${i.ncparNo}]` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {data.inventory && (data.inventory.transfers.length > 0 || data.inventory.used.length > 0 || data.inventory.overdue.length > 0) && (
              <div className="small mb-3" data-testid="dr-inventory">
                <div className="fw-bold text-uppercase text-secondary mb-1">Inventory</div>
                <ul className="list-unstyled mb-0">
                  {data.inventory.transfers.map((t, i) => (
                    <li key={`t${i}`}>
                      <span className="fw-bold">{t.item}</span> ({num(t.qty, t.unit || '')}) · {t.from} → {t.to} ·{' '}
                      {t.status !== 'received' ? 'in transit' : t.discrepancy ? <span className="text-danger fw-semibold">received {num(t.receivedQty)} of {num(t.qty)}</span> : 'received'}
                      {t.storedIn ? ` · stored in ${t.storedIn}` : ''}
                    </li>
                  ))}
                  {data.inventory.used.map((u, i) => (
                    <li key={`u${i}`}>Used up on <span className="fw-bold">{u.location}</span>: {u.item} ({num(u.qty, u.unit || '')})</li>
                  ))}
                  {data.inventory.overdue.map((o, i) => (
                    <li key={`o${i}`} className="text-danger">Awaiting receipt for {o.days} {o.days === 1 ? 'day' : 'days'}: {o.item} ({num(o.qty, o.unit || '')}), {o.from} → {o.to}</li>
                  ))}
                </ul>
              </div>
            )}

            {(data.breakdowns || []).length > 0 && (
              <div className="small mb-3" data-testid="dr-breakdowns">
                <div className="fw-bold text-uppercase text-secondary mb-1">Machinery breakdowns</div>
                <ul className="list-unstyled mb-0">
                  {data.breakdowns.map((b) => (
                    <li key={`${b.tugName}-${b.startedAt}`}>
                      <span className="fw-bold">{b.tugName}:</span> {b.machinery || '—'} — {b.cause || '—'} ·{' '}
                      {b.open ? (
                        <span className="text-danger fw-semibold">open, {b.days} {b.days === 1 ? 'day' : 'days'}</span>
                      ) : (
                        <span>back to normal {when(b.backToNormalAt)}, {b.days} {b.days === 1 ? 'day' : 'days'}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="small">
              <div className="fw-bold text-uppercase text-secondary mb-1">Send history</div>
              {view.runs.length === 0 ? (
                <span className="text-muted" data-testid="dr-no-runs">Not sent yet for this day.</span>
              ) : (
                <ul className="list-unstyled mb-0">
                  {view.runs.map((r) => (
                    <li key={r.id} data-testid="dr-run-row" data-status={r.status}>
                      <span className={`badge ${STATUS[r.status]?.[0] || 'bg-secondary'} me-2`}>{STATUS[r.status]?.[1] || r.status}</span>
                      {when(r.attemptedAt)} · {r.kind === 'auto' ? 'automatic' : `by ${r.triggeredBy || 'a user'}`} · {r.recipientCount} recipient{r.recipientCount === 1 ? '' : 's'}
                      {r.error ? <span className="text-danger"> · {r.error}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
              {!view.emailConfigured && <div className="text-muted mt-1">Outgoing email is not configured on this server; sends are simulated.</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
